// RivalryCard.js
// Drop into: frontend/src/components/RivalryCard.js
//
// Usage in MyIntel.js (full mode):
//   import RivalryCard from '../components/RivalryCard';
//   <RivalryCard userId={userId} />
//
// Usage in profile headers (compact nemesis chip):
//   <RivalryCard userId={manager.user_id} compact />
//
// Mirrors ReputationBadges.js — same self-fetching pattern, same dark surface,
// same compact/full split. Backend: /api/rivalry/{user_id}.

import React, { useEffect, useState } from 'react';
import Icon from './Icon';

// ─────────────────────────────────────────────
// Tier styling (matches rivalry_engine.RIVALRY_TIERS)
// ─────────────────────────────────────────────
const TIER_STYLES = {
  Watching: {
    border: '1px solid rgba(107,114,128,0.4)',
    background: 'rgba(107,114,128,0.1)',
    color: '#9ca3af',
    icon: '👀',
  },
  Active: {
    border: '1px solid rgba(59,130,246,0.4)',
    background: 'rgba(59,130,246,0.1)',
    color: '#60a5fa',
    icon: '⚔️',
  },
  Heated: {
    border: '1px solid rgba(249,115,22,0.4)',
    background: 'rgba(249,115,22,0.1)',
    color: '#fb923c',
    icon: '🔥',
  },
  Personal: {
    border: '1px solid rgba(220,38,38,0.4)',
    background: 'rgba(220,38,38,0.1)',
    color: '#f87171',
    icon: '💢',
  },
};

const tierStyle = (tier) => TIER_STYLES[tier] || TIER_STYLES.Watching;

// ─────────────────────────────────────────────
// Tier chip pill — used in compact mode and rivalry headers
// ─────────────────────────────────────────────
function TierChip({ tier, score, name, compact = false }) {
  const s = tierStyle(tier);
  return (
    <span
      title={`${name} · ${tier} · ${Math.round(score)}/100`}
      style={{
        ...s,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: compact ? '2px 10px' : '4px 12px',
        borderRadius: '9999px',
        fontSize: compact ? '11px' : '12px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      <span><Icon emoji={s.icon} size={14} /></span>
      <span>{name}</span>
      <span style={{ opacity: 0.6, fontWeight: 400 }}>· {tier}</span>
    </span>
  );
}

// ─────────────────────────────────────────────
// Score bar — visualizes rivalry score breakdown
// ─────────────────────────────────────────────
function ScoreBar({ h2h = 0, rejections = 0, blocks = 0 }) {
  const total = h2h + rejections + blocks;
  if (total <= 0) return null;
  const pct = (v) => `${Math.max(0, (v / 100) * 100)}%`;
  return (
    <div
      style={{
        height: '6px',
        borderRadius: '3px',
        background: 'rgba(255,255,255,0.06)',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: pct(h2h), background: '#60a5fa' }} title={`H2H ${h2h.toFixed(0)}`} />
      <div style={{ width: pct(rejections), background: '#fb923c' }} title={`Rejections ${rejections.toFixed(0)}`} />
      <div style={{ width: pct(blocks), background: '#f87171' }} title={`Blocks ${blocks.toFixed(0)}`} />
    </div>
  );
}

// ─────────────────────────────────────────────
// Single signal row (H2H / rejections / blocks)
// ─────────────────────────────────────────────
function SignalRow({ label, value, sub }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '6px 0',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
        {value}
        {sub && (
          <span
            style={{
              fontSize: '11px',
              fontWeight: 400,
              color: 'rgba(255,255,255,0.4)',
              marginLeft: '4px',
            }}
          >
            {sub}
          </span>
        )}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────
// Defining moments list
// ─────────────────────────────────────────────
const MOMENT_ICONS = {
  h2h_blowout: '🥊',
  trade_rejected: '🚫',
  waiver_block: '🎯',
};

function DefiningMoments({ moments = [] }) {
  if (!moments.length) return null;
  return (
    <div>
      <div
        style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.3)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: '8px',
        }}
      >
        Defining Moments
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {moments.map((m, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              padding: '8px 10px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <span style={{ fontSize: '14px', lineHeight: 1.3 }}>
              {MOMENT_ICONS[m.kind] ? <Icon emoji={MOMENT_ICONS[m.kind]} size={14} /> : '•'}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.35,
                }}
              >
                {m.headline}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.45)',
                  marginTop: '2px',
                }}
              >
                {m.detail}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Single rivalry block — used for both nemesis and "other rivalries"
// ─────────────────────────────────────────────
function RivalrySection({ rivalry, isNemesis = false }) {
  const s = tierStyle(rivalry.tier);
  const h2h = rivalry.h2h || {};
  const rej = rivalry.rejections || {};
  const blocks = rivalry.waiver_blocks || {};

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: isNemesis
          ? `1px solid ${s.color}55`
          : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {isNemesis && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: s.color,
              padding: '2px 8px',
              borderRadius: '4px',
              background: s.background,
              border: s.border,
            }}
          >
            Nemesis
          </span>
        )}
        <div
          style={{
            fontSize: '15px',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.95)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          vs {rivalry.opponent_display_name}
        </div>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: s.color,
            padding: '2px 10px',
            borderRadius: '9999px',
            background: s.background,
            border: s.border,
          }}
        >
          {s.icon} {rivalry.tier}
        </span>
      </div>

      {/* Score */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span
            style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Rivalry Score
          </span>
          <span
            style={{
              fontSize: '13px',
              fontWeight: 700,
              color: 'rgba(255,255,255,0.95)',
            }}
          >
            {Math.round(rivalry.score)} / 100
          </span>
        </div>
        <ScoreBar
          h2h={rivalry.h2h_component || 0}
          rejections={rivalry.rejection_component || 0}
          blocks={rivalry.block_component || 0}
        />
      </div>

      {/* Signal table */}
      <div>
        {h2h && h2h.games > 0 && (
          <SignalRow
            label="Head-to-Head"
            value={h2h.record}
            sub={`${h2h.games} game${h2h.games === 1 ? '' : 's'}`}
          />
        )}
        {rej && rej.total > 0 && (
          <SignalRow
            label="Trade Rejections"
            value={rej.total}
            sub={`you sent ${rej.i_offered_rejected} · they sent ${rej.they_offered_rejected}`}
          />
        )}
        {blocks && blocks.total_contests > 0 && (
          <SignalRow
            label="Waiver Blocks"
            value={`${blocks.i_lost} lost`}
            sub={`won ${blocks.i_won}`}
          />
        )}
      </div>

      <DefiningMoments moments={rivalry.defining_moments || []} />
    </div>
  );
}

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────
export default function RivalryCard({
  userId,
  compact = false,
  apiBase = '',
  showOthers = true,
  maxOthers = 3,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    fetch(`${apiBase}/api/rivalry/${userId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [userId, apiBase]);

  if (loading) {
    return (
      <div
        style={{
          color: 'rgba(255,255,255,0.3)',
          fontSize: '12px',
          padding: '8px 0',
        }}
      >
        Loading rivalries...
      </div>
    );
  }

  if (error || !data) {
    return null; // fail silently — same pattern as ReputationBadges
  }

  const nemesis = data.nemesis;
  const rivalries = data.rivalries || [];

  // ── Compact mode: nemesis chip only ───────────────────────
  if (compact) {
    if (!nemesis) return null;
    return (
      <TierChip
        tier={nemesis.tier}
        score={nemesis.score}
        name={nemesis.display_name}
        compact
      />
    );
  }

  // ── Full mode: nemesis card + other rivalries ────────────
  // Empty state when there's nothing to show
  if (!nemesis && rivalries.length === 0) {
    return (
      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '12px',
          color: 'rgba(255,255,255,0.3)',
          textAlign: 'center',
        }}
      >
        No active rivalries yet
      </div>
    );
  }

  const nemesisCard = nemesis
    ? rivalries.find((r) => r.opponent_user_id === nemesis.user_id)
    : null;
  const others = nemesisCard
    ? rivalries.filter((r) => r.opponent_user_id !== nemesis.user_id)
    : rivalries;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3
          style={{
            margin: 0,
            fontSize: '13px',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          Rivalries
        </h3>
        <span
          style={{
            fontSize: '10px',
            color: 'rgba(255,255,255,0.25)',
            letterSpacing: '0.05em',
          }}
        >
          Updated weekly
        </span>
      </div>

      {/* Nemesis card */}
      {nemesisCard && <RivalrySection rivalry={nemesisCard} isNemesis />}

      {/* Other rivalries */}
      {showOthers && others.length > 0 && (
        <>
          <div
            style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Other Rivalries
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {others.slice(0, maxOthers).map((r) => (
              <RivalrySection key={r.opponent_user_id} rivalry={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Named export — small standalone "Nemesis" chip
// for placing next to a name without fetching the full payload.
// Pass the nemesis object straight from /api/rivalry/{id}/nemesis.
// ─────────────────────────────────────────────
export function NemesisChip({ nemesis }) {
  if (!nemesis) return null;
  return (
    <TierChip
      tier={nemesis.tier}
      score={nemesis.score}
      name={nemesis.display_name}
      compact
    />
  );
}
